/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { Modal } from '@blend/consumer-components';
import { ProvideTimers, TimerProps } from '@blend/shared-react';
import React, { useState } from 'react';

import { MobileSlideUpModal } from '@blend/consumer-components';

import { useRootSelector } from '../../../../hooks/useRootSelector';
import { DesktopModal } from './DesktopModal/DesktopModal';
import { MobileModal } from './MobileModal/MobileModal';
import { ModalProps, Upload, UploadResponse, WithUploads } from './types';

export interface DocUploaderModalOwnProps extends WithUploads, ModalProps {
  disabled: boolean;
  isOpen?: boolean;
  imageName?: string;
  uploadFile: (file: File) => Promise<UploadResponse>;
  customAllowedFileTypes: string[];
  label: string;
  message: string;
  onUpload(fileId: string, upload: Upload): void;
  onRemove(fileId: string): void;
}

type DocUploaderModalProps = DocUploaderModalOwnProps & TimerProps;
const DocUploaderModalWithoutTimers: React.FC<DocUploaderModalProps> = ({
  disabled,
  isOpen,
  title,
  imageName,
  uploadFile,
  onSubmit,
  onCancel,
  reactSafeSetInterval,
  fileIds,
  label,
  uploads,
  message,
  onRemove,
  onUpload,
  customAllowedFileTypes,
}) => {
  const { isMobileView } = useRootSelector(state => state.layout);
  const [progress, setProgress] = useState(0.0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  let timerId: number;

  const canSubmit = !isSubmitting;
  const [ModalElement, ModalContent]: [any, any] = isMobileView
    ? [MobileSlideUpModal, MobileModal]
    : [Modal, DesktopModal];

  const handleSubmit = async () => {
    /**
     * The formula used in getNewProgress gives a good upload curve:
     * it starts quickly, but tapers off without ever hitting the end.
     * @see https://math.stackexchange.com/questions/869150/a-function-f-such-that-fx-increases-from-0-to-1-when-x-increases-fro#answer-869157
     **/
    // TODO Use an actual submission status instead of a fake one
    const oneSecond = 1000;
    const startTime = Date.now();
    const getNewProgress = () => {
      const currentTimeDiff = Date.now() - startTime;
      return currentTimeDiff / (currentTimeDiff + oneSecond);
    };

    // Mock progress using a timer that gets slower and slower...
    timerId = reactSafeSetInterval(() => {
      setProgress(getNewProgress());
      // setProgress(alpha + (1 - alpha) * progress);
    }, 100);
    setIsSubmitting(true);
    try {
      await onSubmit();
      setProgress(1.0);
      setIsSubmitting(false);
      resetState();
    } catch (error) {
      setProgress(0.0);
      setIsSubmitting(false);
      setErrorMessage((error as Error).message);
    } finally {
      // regardless of what happens, clear the interval
      clearInterval(timerId);
    }
  };

  const handleCancel = () => {
    resetState();
    onCancel();
  };

  const resetState = () => {
    setProgress(0.0);
    setIsSubmitting(false);
    setErrorMessage(undefined);
    clearInterval(timerId);
  };

  return (
    <ModalElement
      handleClose={handleCancel}
      contentLabel="Upload additional documents"
      isOpen={!!isOpen}
      headerText={title}
      headerFont="UntitledSans"
    >
      <ModalContent
        fileIds={fileIds}
        progress={progress}
        isSubmitting={isSubmitting}
        errorMessage={errorMessage}
        disabled={disabled}
        canSubmit={canSubmit}
        multiple={false}
        imageName={imageName}
        uploadFile={uploadFile}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        onUpload={onUpload}
        onRemove={onRemove}
        customAllowedFileTypes={customAllowedFileTypes}
        label={label}
        title={title}
        uploads={uploads}
        message={message}
      />
    </ModalElement>
  );
};

export const DocUploaderModal = (props: DocUploaderModalOwnProps) => (
  <ProvideTimers render={timerProps => <DocUploaderModalWithoutTimers {...props} {...timerProps} />} />
);
